import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import { faAngleDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { secondsToTime } from '@soundxyz/utils/helpers';
import { Fade } from '@components/Song/shared';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { MediaQuery } from '@context/mediaQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const MOBILE_CARD_HEIGHT_AUDIENCE = 96;
const CARD_HEIGHT_NEW_WAVEFORM = 86;

const MAX_COMMENT_SHOW_LENGTH_MOBILE = 45;
const MAX_COMMENT_SHOW_LENGTH_NEW_WAVEFORM = 74;

const AVATAR_SIZE_NEW_WAVEFORM = 48;

export const BlackCommentCard = ({
  username,
  publicAddress,
  comment,
  avatarUrl,
  isArtist,
  webappUri,
  audience,
  ...props
}: {
  username: string;
  publicAddress: string;
  comment?: string;
  avatarUrl?: string | null;
  isArtist: boolean;
  webappUri: string;
  audience?: boolean;
} & (
  | {
      commentTimestamp?: number;
      datePurchased?: Date;
    }
  | {
      totalEditions?: number;
    }
)) => {
  const [expanded, setExpanded] = useState(false);
  const { tablet } = MediaQuery.useContainer();

  const commentTimestamp = 'commentTimestamp' in props ? props.commentTimestamp : undefined;
  const datePurchased = 'datePurchased' in props ? props.datePurchased : undefined;

  const totalEditions = 'totalEditions' in props ? props.totalEditions : undefined;

  const maxCommentHeight = audience ? MOBILE_CARD_HEIGHT_AUDIENCE : CARD_HEIGHT_NEW_WAVEFORM;

  const MAX_COMMENT_SHOW_LENGTH = !tablet
    ? MAX_COMMENT_SHOW_LENGTH_MOBILE
    : MAX_COMMENT_SHOW_LENGTH_NEW_WAVEFORM;

  const hide = !!comment && comment.length > MAX_COMMENT_SHOW_LENGTH;

  const animationVariants: Variants = {
    expand: {
      height: 'auto',
      transition: { duration: 0.2, delay: 0.1 },
    },
    collapse: {
      height: hide ? maxCommentHeight : 'auto',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  const animationProps = hide
    ? {
        initial: 'collapse',
        animate: expanded ? 'expand' : 'collapse',
        variants: animationVariants,
        transition: { ease: 'easeOut' },
      }
    : null;

  const noComment = !comment;
  const userInfo = (
    <div>
      <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
        <div className="flex items-center gap-1.5">
          <h2
            className={clsx(
              'overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-m font-semibold text-white',
              noComment ? 'max-w-[250px]' : 'max-w-[200px]',
            )}
          >
            {username}
          </h2>

          {isArtist && <VerifiedTooltip noTooltip size={18} />}
        </div>
      </AccessibleLink>
    </div>
  );

  return (
    <motion.div
      {...animationProps}
      className={clsx(
        'relative flex h-auto max-w-[355px] overflow-hidden rounded-md bg-neutral900',
        noComment ? 'items-center' : 'items-start',
        audience ? 'w-full p-4 pr-5' : 'w-[350px] px-1 py-2',
      )}
    >
      <div className={clsx('flex', noComment && 'items-center justify-center')}>
        <div className="mr-2 h-12 w-12">
          <Avatar
            size={AVATAR_SIZE_NEW_WAVEFORM}
            src={avatarUrl}
            username={username}
            borderRadius="50%"
            walletAddress={publicAddress}
            hasShadow
            webappUri={webappUri}
          />
        </div>
      </div>

      <motion.div className=" flex h-auto w-full flex-col overflow-hidden">
        {userInfo}
        <div className="flex items-center gap-1 font-base text-base-xs text-base100 opacity-50">
          {!!commentTimestamp && <span>{secondsToTime(commentTimestamp)}</span>}
          {!!commentTimestamp && !!datePurchased && <span>•</span>}
          {!!datePurchased && <span>{format(datePurchased, 'MM/dd/yy')}</span>}
          {!!totalEditions && (!!datePurchased || !!commentTimestamp) && <span>•</span>}
          {!!totalEditions && (
            <>
              <span>
                {totalEditions}
                &nbsp;
                {getSingularOrPlural({
                  word: 'edition',
                  count: totalEditions,
                })}
              </span>
            </>
          )}
        </div>

        {!noComment && (
          <p
            className={clsx(
              'mt-2 h-auto flex-grow overflow-hidden text-ellipsis font-base text-base-m text-white ',
              !!hide && 'cursor-pointer',
            )}
            onClick={() => setExpanded(prev => !prev)}
          >
            {comment}
          </p>
        )}

        {!!hide && !expanded && (
          <>
            <motion.div
              className="pointer-events-none absolute bottom-0 h-10 w-full"
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <Fade length={15} lengthStartColor="#171717" lengthEndColor="rgba(23, 23, 23, 0)" />
            </motion.div>
            <motion.div
              className={clsx('absolute -bottom-3 py-3 pb-4', audience ? 'right-2' : 'right-1')}
              transition={{ duration: 0.2, delay: 0.1 }}
              onClick={e => {
                e.stopPropagation();
                setExpanded(prev => !prev);
              }}
            >
              <FontAwesomeIcon icon={faAngleDown} color="white" />
            </motion.div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};
